import { getEffectiveTranslations } from './i18n';

function t(lang,key){return getEffectiveTranslations(lang||'en')[key]||key;}

// BeeLove — Bee Journey System
// 6 stages, 4 roles, 5 moods, Hive Score (legacy/community badge)

// ── BEE JOURNEY — 6 stages ────────────────────────────────────────────────
export function getBEE_STAGES(lang){return [
  {
    id: 'registered',
    name: t(lang,'journey.s1_label'),
    icon: 'registered',
    color: '#9CA880',
    desc: t(lang,'journey.s1_desc'),
    reward: t(lang,'journey.s1_reward'),
    check: (user) => true, // Always complete — if you're viewing this, you're registered
  },
  {
    id: 'scored',
    name: t(lang,'journey.s2_label'),
    icon: 'scored',
    color: '#C9A84C',
    desc: t(lang,'journey.s2_desc'),
    reward: t(lang,'journey.s3_reward'),
    check: (user) => (user?.votes || 0) >= 5,
  },
  {
    id: 'known',
    name: t(lang,'journey.s3_label'),
    icon: 'known',
    color: '#E2C06E',
    desc: t(lang,'journey.s3_desc_full'),
    reward: t(lang,'journey.s5_reward'),
    check: (user) => {
      if (!user) return false;
      const hasBio      = (user.bio?.length || 0) > 30;
      const hasPrompts  = user.prompt1 && user.prompt2;
      const hasBasics   = user.height || user.education || user.job;
      return hasBio && hasPrompts && hasBasics;
    },
  },
  {
    id: 'connected',
    name: t(lang,'journey.s4_label'),
    icon: 'connected',
    color: '#F0A500',
    desc: t(lang,'journey.s4_desc_full'),
    reward: t(lang,'journey.s4_reward'),
    check: (user, conversations, matches) => {
      if (!conversations || !matches?.length) return false;
      return matches.some(m => (conversations[m.id]?.length || 0) >= 10);
    },
  },
  {
    id: 'hive',
    name: t(lang,'journey.s5_hive_label'),
    icon: 'hive',
    color: '#C9A84C',
    desc: t(lang,'journey.s5_friend_desc'),
    reward: t(lang,'journey.s5_hive_reward'),
    check: (user) => (user?.inviteCount || 0) >= 3,
  },
  {
    id: 'loved',
    name: t(lang,'journey.s5_label'),
    icon: 'loved',
    color: '#FFD700',
    desc: t(lang,'journey.s6_desc'),
    reward: t(lang,'journey.s6_reward'),
    check: (user, conversations, matches) => {
      const highScore   = (user?.score || 0) >= 9.0;
      const superLikes  = (user?.superLikes || 0) >= 3;
      const greatMatch  = matches?.some(m => m.compatScore >= 90);
      return highScore || superLikes || greatMatch;
    },
  },
];
}
export const BEE_STAGES = getBEE_STAGES('en');


// ── BEE ROLES — auto-assigned by behaviour ────────────────────────────────
export function getBeeRoles(lang) { return {
  scout: {
    id: 'scout',
    name: t(lang,'role.scout'),
    icon: 'scout',
    color: '#7AB8C4',
    desc: t(lang,'role.scout_desc'),
    check: (user, stats) => (stats?.swipes || 0) >= 50,
  },
  builder: {
    id: 'builder',
    name: t(lang,'role.builder'),
    icon: 'builder',
    color: '#9CA880',
    desc: t(lang,'role.builder_desc'),
    check: (user) => (user?.inviteCount || 0) >= 3,
  },
  connector: {
    id: 'connector',
    name: t(lang,'role.connector'),
    icon: 'connector',
    color: '#C9A84C',
    desc: t(lang,'role.connector_desc'),
    check: (user, stats, conversations, matches) => {
      const longConvos = matches?.filter(m => (conversations?.[m.id]?.length || 0) >= 20)?.length || 0;
      return longConvos >= 2;
    },
  },
  queenbee: {
    id: 'queenbee',
    name: t(lang,'role.queenbee'),
    icon: 'queen',
    color: '#FFD700',
    desc: t(lang,'role.queenbee_desc'),
    check: (user) => (user?.score || 0) >= 9.2 && (user?.votes || 0) >= 10,
  },
}; }
export const BEE_ROLES = getBeeRoles('en');
export function computeRole(user, stats, conversations, matches) {
  // Priority order — queen > connector > builder > scout
  for (const role of ['queenbee','connector','builder','scout']) {
    if (BEE_ROLES[role].check(user, stats, conversations, matches)) {
      return BEE_ROLES[role];
    }
  }
  return null; // no role yet
}

// ── BEE MOODS — weekly state ───────────────────────────────────────────────
export function getBeeModsArr(lang){ return [
  { id:'growing',    icon:'growing',    label:t(lang,'mood.growing'),    desc:t(lang,'mood.growing_desc'),    color:'#9CA880' },
  { id:'exploring',  icon:'exploring',  label:t(lang,'mood.exploring'),  desc:t(lang,'mood.exploring_desc'),  color:'#7AB8C4' },
  { id:'ready',      icon:'ready',      label:t(lang,'mood.ready'),      desc:t(lang,'mood.ready_desc'),      color:'#C9A84C' },
  { id:'connecting', icon:'connecting', label:t(lang,'mood.connecting'), desc:t(lang,'mood.connecting_desc'), color:'#A084C4' },
  { id:'lowkey',     icon:'lowkey',     label:t(lang,'mood.low_key'),    desc:t(lang,'mood.lowkey_desc'),     color:'#888880' },
]; }
export const BEE_MOODS = getBeeModsArr('en');

export function getMoodById(id) {
  return BEE_MOODS.find(m => m.id === id) || BEE_MOODS[0];
}

// ── BEE JOURNEY PROGRESS ──────────────────────────────────────────────────
export function computeJourneyProgress(user, conversations = {}, matches = [], lang = 'en') {
  return getBEE_STAGES(lang).map(stage => ({
    ...stage,
    completed: stage.check(user, conversations, matches),
  }));
}

export function getJourneyStage(user, conversations = {}, matches = [], lang = 'en') {
  const progress = computeJourneyProgress(user, conversations, matches, lang);
  const completed = progress.filter(s => s.completed);
  return {
    progress,
    completedCount: completed.length,
    currentStage: completed[completed.length - 1] || null,
    nextStage: progress.find(s => !s.completed) || null,
    isComplete: completed.length === getBEE_STAGES(lang).length,
  };
}

// ── HIVE SCORE — community legacy badge ───────────────────────────────────
// Cumulative, never decreases. Computed from contributions.
export function computeHiveScore(user, stats = {}) {
  let score = 0;
  score += Math.min((user?.votes || 0) * 2, 20);         // friend ratings
  score += Math.min((user?.inviteCount || 0) * 5, 25);    // invites
  score += Math.min((stats?.matches || 0) * 3, 15);       // real matches
  score += Math.min((stats?.convos || 0) * 2, 20);        // conversations
  score += (user?.score || 0) >= 9.0 ? 10 : 0;           // high score
  score += (user?.score || 0) >= 9.5 ? 10 : 0;           // exceptional score
  return Math.min(Math.round(score), 100);
}

export function hiveScoreTier(score) {
  if (score >= 80) return { labelKey:'hive.tier_elder',  color:'#FFD700', icon:'elder' };
  if (score >= 55) return { labelKey:'hive.tier_pillar', color:'#C9A84C', icon:'star' };
  if (score >= 30) return { labelKey:'hive.tier_member', color:'#9CA880', icon:'member' };
  return               { labelKey:'hive.tier_newbee',  color:'#888880', icon:'grow' };
}

// ── HIVE EVENTS — Weekly Questions + Seasons ──────────────────────────────
export const HIVE_SEASONS = [
  { id:'depth',      labelKey:'hive.season_depth',      icon:'depth',      color:'#7AB8C4', months:[1,2]  },
  { id:'adventure',  labelKey:'hive.season_adventure',  icon:'adventure',  color:'#C9A84C', months:[3,4,5] },
  { id:'connection', labelKey:'hive.season_connection', icon:'connection', color:'#E2C06E', months:[6,7,8] },
  { id:'reflection', labelKey:'hive.season_reflection', icon:'reflection', color:'#A084C4', months:[9,10,11,12] },
];

export function getCurrentSeason() {
  const month = new Date().getMonth() + 1;
  return HIVE_SEASONS.find(s => s.months.includes(month)) || HIVE_SEASONS[0];
}

export function getWeeklyQuestions(lang) { return [
  { id:'q1', text:t(lang,'hive.wq1'), season:'connection' },
  { id:'q2', text:t(lang,'hive.wq2'), season:'depth'      },
  { id:'q3', text:t(lang,'hive.wq3'), season:'depth'      },
  { id:'q4', text:t(lang,'hive.wq4'), season:'reflection' },
  { id:'q5', text:t(lang,'hive.wq5'), season:'adventure'  },
  { id:'q6', text:t(lang,'hive.wq6'), season:'reflection' },
  { id:'q7', text:t(lang,'hive.wq7'), season:'connection' },
  { id:'q8', text:t(lang,'hive.wq8'), season:'depth'      },
]; }
export const WEEKLY_QUESTIONS = getWeeklyQuestions('en');

export function getCurrentWeeklyQuestion(lang = 'en') {
  const week = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
  return getWeeklyQuestions(lang)[week % 8];
}

// ── HIVE STORIES (mock) ───────────────────────────────────────────────────
export function getHiveStories(lang) { return [
  {
    id:'s1',
    type:'match',
    icon:'ready',
    text:t(lang,'hive.story1'),
    city:'Montréal',
    daysAgo:12,
    anonymous:true,
  },
  {
    id:'s2',
    type:'friendship',
    icon:'connecting',
    text:"A Scout and a Builder discovered they lived 3 blocks apart and shared a dog trainer.",
    city:'Paris',
    daysAgo:5,
    anonymous:true,
  },
  {
    id:'s3',
    type:'match',
    icon:'loved',
    text:t(lang,'hive.story3'),
    city:'Lyon',
    daysAgo:20,
    anonymous:true,
  },
  {
    id:'s4',
    type:'community',
    icon:'hive',
    text:"A Builder invited 7 friends. Three of them are now active members. One just got their first match.",
    city:'Montréal',
    daysAgo:3,
    anonymous:true,
  },
  {
    id:'s5',
    type:'match',
    icon:'ready',
    text:"They both answered this week's question with almost the exact same words. That was their first conversation.",
    city:'Nice',
    daysAgo:8,
    anonymous:true,
  },
  {
    id:'s6',
    type:'friendship',
    icon:'connecting',
    text:"Both in 'Connecting' mood, both Builders — they met for coffee as friends first. Still meet every week.",
    city:'Bordeaux',
    daysAgo:31,
    anonymous:true,
  },
]; }
export const HIVE_STORIES = getHiveStories('en');